import React, { useEffect, useRef, useState } from 'react';
import api from '../api';
import PageHeader from '../components/PageHeader';
import { Ic, Spinner, StatCard, fmtNum, useToast } from '../components/ui';

const TRIGGER_TYPES = [
  { value: 'new_user', label: 'Yeni kullanıcı', desc: '/start ile ilk kez gelen', ic: 'userPlus', c: '#56c26a' },
  { value: 'bot_start', label: 'Bot başlatıldı', desc: 'Her /start komutu', ic: 'play', c: '#6ab3f3' },
  { value: 'new_message', label: 'Yeni mesaj', desc: 'Sohbet modunda mesaj', ic: 'chat', c: '#a695e7' },
  { value: 'new_application', label: 'Yeni başvuru', desc: 'Form tamamlandı', ic: 'clipboard', c: '#e5a443' },
  { value: 'keyword', label: 'Anahtar kelime', desc: 'Mesajda belirli kelime', ic: 'search', c: '#ef5b5b' },
];
const trigMap = Object.fromEntries(TRIGGER_TYPES.map(t => [t.value, t]));
const VARS = ['{{bot_name}}', '{{user_name}}', '{{user_id}}', '{{message}}', '{{position}}', '{{time}}'];

const TEMPLATES = {
  new_user: '👤 Yeni kullanıcı!\n\nBot: {{bot_name}}\nKullanıcı: {{user_name}}\nID: {{user_id}}\nZaman: {{time}}',
  bot_start: '🚀 Bot başlatıldı!\n\nBot: {{bot_name}}\nKullanıcı: {{user_name}}\nZaman: {{time}}',
  new_message: '💬 Yeni mesaj!\n\nBot: {{bot_name}}\nKullanıcı: {{user_name}}\nMesaj: {{message}}\nZaman: {{time}}',
  new_application: '📋 Yeni başvuru!\n\nBot: {{bot_name}}\nKullanıcı: {{user_name}}\nPozisyon: {{position}}\nZaman: {{time}}',
  keyword: '🔍 Anahtar kelime!\n\nBot: {{bot_name}}\nKullanıcı: {{user_name}}\nMesaj: {{message}}\nZaman: {{time}}',
};
const EMPTY_RULE = { channel_id: '', bot_id: '', trigger_type: 'new_user', trigger_value: '', template: '', quiet_hours_start: '', quiet_hours_end: '', is_active: true };
const EMPTY_CHANNEL = { name: '', type: 'telegram', config: { token: '', chat_id: '' } };

const CSS = `
.nt-trig { display:grid; grid-template-columns:repeat(auto-fill, minmax(min(100%, 150px), 1fr)); gap:6px; }
.nt-opt { display:flex; align-items:center; gap:8px; padding:8px 10px; border-radius:10px; cursor:pointer; border:1.5px solid var(--border3); background:var(--surface2); color:inherit; font:inherit; text-align:left; min-width:0; transition:border-color .12s, background .12s; }
.nt-opt:hover { border-color:var(--border); }
.nt-opt.on { border-color:var(--accent); background:rgba(82,136,193,.14); }
.nt-rule { display:flex; gap:14px; padding:14px 16px; border-bottom:1px solid var(--border3); align-items:flex-start; min-width:0; }
.nt-rule:last-child { border-bottom:none; }
.nt-rule.off { opacity:.55; }
.nt-tpl { font-family:"JetBrains Mono",Consolas,Menlo,monospace; font-size:12.5px; color:var(--text2); background:var(--surface3); border-radius:10px; padding:8px 10px; white-space:pre-wrap; word-break:break-word; max-height:76px; overflow:hidden; position:relative; margin-top:8px; }
.nt-tpl::after { content:''; position:absolute; left:0; right:0; bottom:0; height:22px; background:linear-gradient(transparent, var(--surface3)); }
.nt-flow { display:flex; align-items:center; gap:6px; flex-wrap:wrap; font-size:13px; color:var(--text3); }
.nt-acts { display:flex; align-items:center; gap:2px; flex-shrink:0; }
@media (max-width: 768px) {
  .nt-rule { flex-wrap:wrap; padding:12px; }
  .nt-rule .nt-body { flex:1 1 calc(100% - 60px); }
  .nt-acts { width:100%; justify-content:flex-end; border-top:1px solid var(--border3); padding-top:8px; }
}
`;

function RuleForm({ ruleForm, setRuleForm, editRule, channels, bots, saving, error, onSubmit, onClose }) {
  const tplRef = useRef(null);
  function insertVar(v) {
    const el = tplRef.current;
    const t = ruleForm.template || '';
    if (!el) { setRuleForm(f => ({ ...f, template: t + v })); return; }
    const a = el.selectionStart ?? t.length, b = el.selectionEnd ?? t.length;
    setRuleForm(f => ({ ...f, template: t.slice(0, a) + v + t.slice(b) }));
    setTimeout(() => { el.focus(); el.setSelectionRange(a + v.length, a + v.length); }, 0);
  }
  const qs = ruleForm.quiet_hours_start, qe = ruleForm.quiet_hours_end;
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 580, width: '100%' }}>
        <h3 className="flex-center gap-8"><Ic n={editRule ? 'edit' : 'bell'} s={20} c="var(--link)" />{editRule ? 'Kuralı düzenle' : 'Yeni bildirim kuralı'}</h3>
        {error && <div style={{ background: 'var(--danger-s)', color: 'var(--danger)', borderRadius: 10, padding: '8px 12px', fontSize: 13.5, marginBottom: 12 }}>{error}</div>}
        <form onSubmit={onSubmit}>
          <div className="input-group">
            <label>Ne zaman?</label>
            <div className="nt-trig">
              {TRIGGER_TYPES.map(t => (
                <button type="button" key={t.value} className={'nt-opt' + (ruleForm.trigger_type === t.value ? ' on' : '')}
                  onClick={() => setRuleForm(f => ({ ...f, trigger_type: t.value, template: (!f.template || Object.values(TEMPLATES).includes(f.template)) ? TEMPLATES[t.value] : f.template }))}>
                  <span className="ic-circle" style={{ width: 30, height: 30, background: t.c + '22', color: t.c }}><Ic n={t.ic} s={16} /></span>
                  <span style={{ minWidth: 0 }}>
                    <span className="truncate" style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{t.label}</span>
                    <span className="truncate" style={{ display: 'block', fontSize: 11.5, color: 'var(--text3)' }}>{t.desc}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          {ruleForm.trigger_type === 'keyword' && (
            <div className="input-group">
              <label>Anahtar kelime</label>
              <input className="input" value={ruleForm.trigger_value} onChange={e => setRuleForm(f => ({ ...f, trigger_value: e.target.value }))} placeholder="Örn: para, yardım, acil" required />
            </div>
          )}

          <div className="form-2">
            <div className="input-group">
              <label>Bot</label>
              <select className="input" value={ruleForm.bot_id} onChange={e => setRuleForm(f => ({ ...f, bot_id: e.target.value }))} required>
                <option value="">Seçin</option>
                {bots.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div className="input-group">
              <label>Bildirim kanalı</label>
              <select className="input" value={ruleForm.channel_id} onChange={e => setRuleForm(f => ({ ...f, channel_id: e.target.value }))} required>
                <option value="">Seçin</option>
                {channels.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          <div className="input-group">
            <label>Bildirim şablonu</label>
            <textarea ref={tplRef} className="input mono" value={ruleForm.template} onChange={e => setRuleForm(f => ({ ...f, template: e.target.value }))} style={{ minHeight: 130, fontSize: 13 }} required />
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
              {VARS.map(v => <button type="button" key={v} className="btn btn-xs btn-teal mono" onClick={() => insertVar(v)}>{v}</button>)}
            </div>
          </div>

          <div className="input-group" style={{ marginBottom: 6 }}>
            <label className="flex-center gap-6"><Ic n="moon" s={14} />Sessiz saatler (opsiyonel)</label>
            <div className="flex-center gap-8">
              <input type="number" className="input" min="0" max="23" value={qs} onChange={e => setRuleForm(f => ({ ...f, quiet_hours_start: e.target.value }))} placeholder="23" style={{ flex: 1 }} />
              <span className="text-muted">—</span>
              <input type="number" className="input" min="0" max="23" value={qe} onChange={e => setRuleForm(f => ({ ...f, quiet_hours_end: e.target.value }))} placeholder="8" style={{ flex: 1 }} />
            </div>
          </div>
          <div className="text-sm" style={{ color: qs || qe ? 'var(--warning)' : 'var(--text3)', marginBottom: 18 }}>
            {qs || qe ? `${qs || 0}:00 — ${qe || 0}:00 arası bildirim gönderilmez` : 'Boş bırakılırsa her saatte gönderilir'}
          </div>

          <div className="flex gap-8" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>İptal</button>
            <button type="submit" className="btn btn-primary" disabled={saving}><Ic n="save" s={16} />{saving ? 'Kaydediliyor…' : 'Kaydet'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ChannelForm({ channelForm, setChannelForm, saving, error, onSubmit, onClose }) {
  const tg = channelForm.type === 'telegram';
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h3 className="flex-center gap-8"><Ic n="radio" s={20} c="var(--link)" />Bildirim kanalı ekle</h3>
        {error && <div style={{ background: 'var(--danger-s)', color: 'var(--danger)', borderRadius: 10, padding: '8px 12px', fontSize: 13.5, marginBottom: 12 }}>{error}</div>}
        <form onSubmit={onSubmit}>
          <div className="seg" style={{ marginBottom: 14, width: '100%' }}>
            {[['telegram', 'Telegram bot', 'send'], ['webhook', 'Webhook URL', 'webhook']].map(([v, l, ic]) => (
              <button type="button" key={v} className={channelForm.type === v ? 'on' : ''} style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                onClick={() => setChannelForm(f => ({ ...f, type: v, config: {} }))}><Ic n={ic} s={15} />{l}</button>
            ))}
          </div>
          <div className="input-group">
            <label>Kanal adı</label>
            <input className="input" value={channelForm.name} onChange={e => setChannelForm(f => ({ ...f, name: e.target.value }))} placeholder="Örn: Admin grubu bildirimleri" required />
          </div>
          {tg ? (
            <>
              <div className="input-group">
                <label>Bot token</label>
                <input className="input mono" type="password" value={channelForm.config.token || ''} onChange={e => setChannelForm(f => ({ ...f, config: { ...f.config, token: e.target.value } }))} placeholder="1234567890:AAxxxxxx" required autoComplete="off" />
                <div className="text-muted text-xs" style={{ marginTop: 4 }}>@BotFather'dan yeni bot oluşturun veya mevcut botu kullanın</div>
              </div>
              <div className="input-group" style={{ marginBottom: 18 }}>
                <label>Chat ID</label>
                <input className="input mono" value={channelForm.config.chat_id || ''} onChange={e => setChannelForm(f => ({ ...f, config: { ...f.config, chat_id: e.target.value } }))} placeholder="-1001234567890" required />
                <div className="text-muted text-xs" style={{ marginTop: 4 }}>@userinfobot'a mesaj atarak ID'nizi öğrenin. Grup ID'leri - ile başlar.</div>
              </div>
            </>
          ) : (
            <div className="input-group" style={{ marginBottom: 18 }}>
              <label>Webhook URL</label>
              <input className="input mono" value={channelForm.config.url || ''} onChange={e => setChannelForm(f => ({ ...f, config: { ...f.config, url: e.target.value } }))} placeholder="https://…" required />
            </div>
          )}
          <div className="flex gap-8" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>İptal</button>
            <button type="submit" className="btn btn-primary" disabled={saving}><Ic n="plus" s={16} />{saving ? 'Ekleniyor…' : 'Ekle'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Notifications() {
  const [tab, setTab] = useState('rules');
  const [channels, setChannels] = useState([]);
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddChannel, setShowAddChannel] = useState(false);
  const [showAddRule, setShowAddRule] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [channelForm, setChannelForm] = useState(EMPTY_CHANNEL);
  const [ruleForm, setRuleForm] = useState(EMPTY_RULE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toastEl, toast] = useToast();

  async function load() {
    setLoading(true);
    try {
      const [cr, rr, lr, br] = await Promise.all([api.get('/notifications/channels'), api.get('/notifications/rules'), api.get('/notifications/logs'), api.get('/bots')]);
      setChannels(cr.data || []); setRules(rr.data || []); setLogs(lr.data || []); setBots(br.data || []);
    } catch (e) { /* noop */ }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function saveChannel(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.post('/notifications/channels', channelForm);
      setShowAddChannel(false); setChannelForm(EMPTY_CHANNEL); toast('Kanal eklendi'); load();
    } catch (err) { setError(err.response?.data?.error || 'Hata'); }
    setSaving(false);
  }
  async function deleteChannel(id) {
    if (!window.confirm('Bu kanal silinsin mi?')) return;
    try { await api.delete(`/notifications/channels/${id}`); toast('Silindi'); load(); } catch (e) { toast('Silinemedi', 'err'); }
  }
  async function saveRule(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (editRule) { await api.put(`/notifications/rules/${editRule.id}`, ruleForm); setEditRule(null); } else { await api.post('/notifications/rules', ruleForm); setShowAddRule(false); }
      setRuleForm(EMPTY_RULE); toast('Kural kaydedildi'); load();
    } catch (err) { setError(err.response?.data?.error || 'Hata'); }
    setSaving(false);
  }
  async function deleteRule(id) {
    if (!window.confirm('Bu kural silinsin mi?')) return;
    try { await api.delete(`/notifications/rules/${id}`); toast('Silindi'); load(); } catch (e) { toast('Silinemedi', 'err'); }
  }
  async function toggleRule(rule) {
    setRules(rs => rs.map(r => (r.id === rule.id ? { ...r, is_active: !r.is_active } : r)));
    try { await api.put(`/notifications/rules/${rule.id}`, { ...rule, is_active: !rule.is_active }); load(); } catch (e) { toast('Değiştirilemedi', 'err'); load(); }
  }
  function openAddRule() {
    setRuleForm({ ...EMPTY_RULE, channel_id: channels[0]?.id || '', bot_id: bots[0]?.id || '', template: TEMPLATES.new_user });
    setEditRule(null); setShowAddRule(true); setError('');
  }
  function openEditRule(rule) {
    setRuleForm({
      channel_id: rule.channel_id, bot_id: rule.bot_id, trigger_type: rule.trigger_type, trigger_value: rule.trigger_value || '',
      template: rule.template, quiet_hours_start: rule.quiet_hours_start ?? '', quiet_hours_end: rule.quiet_hours_end ?? '', is_active: rule.is_active,
    });
    setEditRule(rule); setError('');
  }
  const closeRuleForm = () => { setShowAddRule(false); setEditRule(null); };
  const sentCount = logs.filter(l => l.status === 'sent').length;
  const failCount = logs.length - sentCount;
  const quiet = r => (r.quiet_hours_start !== null && r.quiet_hours_start !== undefined && r.quiet_hours_start !== '') || (r.quiet_hours_end !== null && r.quiet_hours_end !== undefined && r.quiet_hours_end !== '');

  return (
    <div className="page-enter">
      <style>{CSS}</style>{toastEl}
      <PageHeader
        title="Bildirimler"
        subtitle="Bot olaylarında Telegram grubuna veya webhook'a otomatik bildirim"
        actions={tab === 'rules' ? <button className="btn btn-primary" onClick={openAddRule} disabled={!channels.length || !bots.length}><Ic n="plus" s={18} />Yeni kural</button>
          : tab === 'channels' ? <button className="btn btn-primary" onClick={() => { setShowAddChannel(true); setError(''); }}><Ic n="plus" s={18} />Kanal ekle</button> : null}
      />

      <div className="stats-grid" style={{ marginBottom: 14 }}>
        <StatCard icon="bell" label="Aktif kural" value={`${rules.filter(r => r.is_active).length}/${rules.length}`} onClick={() => setTab('rules')} />
        <StatCard icon="radio" label="Kanal" value={fmtNum(channels.length)} color="var(--purple)" bg="var(--purple-s)" onClick={() => setTab('channels')} />
        <StatCard icon="checkCircle" label="Gönderildi" value={fmtNum(sentCount)} color="var(--success)" bg="var(--success-s)" onClick={() => setTab('logs')} sub="son kayıtlar" />
        <StatCard icon="alert" label="Başarısız" value={fmtNum(failCount)} color="var(--danger)" bg="var(--danger-s)" onClick={() => setTab('logs')} sub="son kayıtlar" />
      </div>

      <div className="tabs" style={{ marginBottom: 16 }}>
        {[['rules', 'Kurallar', 'bell', rules.length], ['channels', 'Kanallar', 'radio', channels.length], ['logs', 'Geçmiş', 'history', logs.length]].map(([k, l, ic, c]) => (
          <button key={k} className={'tab' + (tab === k ? ' active' : '')} onClick={() => setTab(k)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <Ic n={ic} s={17} />{l}<span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)' }}>{c}</span>
          </button>
        ))}
      </div>

      {loading ? <div className="card" style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div> : (
        <>
          {/* ═══ KURALLAR ═══ */}
          {tab === 'rules' && (
            <>
              {!channels.length && (
                <div className="card flex-center gap-12" style={{ marginBottom: 14, borderColor: 'rgba(229,164,67,.35)', background: 'var(--warning-s)', flexWrap: 'wrap' }}>
                  <Ic n="info" s={22} c="var(--warning)" />
                  <span style={{ flex: '1 1 200px' }}>Kural eklemeden önce bildirimlerin gideceği bir kanal ekleyin.</span>
                  <button className="btn btn-sm btn-primary" onClick={() => { setTab('channels'); setShowAddChannel(true); setError(''); }}><Ic n="plus" s={15} />Kanal ekle</button>
                </div>
              )}
              {!rules.length ? (
                <div className="card"><div className="empty-state"><div className="icon"><Ic n="bell" s={44} /></div><p>Henüz kural yok</p>
                  {channels.length > 0 && <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={openAddRule}><Ic n="plus" s={16} />Kural ekle</button>}</div></div>
              ) : (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  {rules.map(rule => {
                    const t = trigMap[rule.trigger_type] || { label: rule.trigger_type, ic: 'bell', c: '#6ab3f3' };
                    return (
                      <div key={rule.id} className={'nt-rule' + (rule.is_active ? '' : ' off')}>
                        <div className="ic-circle" style={{ background: t.c + '22', color: t.c }}><Ic n={t.ic} s={21} /></div>
                        <div className="nt-body" style={{ flex: 1, minWidth: 0 }}>
                          <div className="flex-center gap-8" style={{ flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600, fontSize: 15 }}>{t.label}</span>
                            {rule.trigger_type === 'keyword' && rule.trigger_value && <span className="chip" style={{ background: 'var(--danger-s)', color: 'var(--danger)' }}>"{rule.trigger_value}"</span>}
                            {!rule.is_active && <span className="badge badge-neutral">Kapalı</span>}
                          </div>
                          <div className="nt-flow" style={{ marginTop: 2 }}>
                            <span className="flex-center gap-4"><Ic n="bot" s={14} />{rule.bot_name || '—'}</span>
                            <Ic n="arrowRight" s={13} />
                            <span className="flex-center gap-4" style={{ color: 'var(--link)' }}><Ic n="radio" s={14} />{rule.channel_name || '—'}</span>
                            {quiet(rule) && <span className="flex-center gap-4" style={{ color: 'var(--warning)' }}><Ic n="moon" s={13} />{rule.quiet_hours_start}:00–{rule.quiet_hours_end}:00</span>}
                          </div>
                          {rule.template && <div className="nt-tpl">{rule.template}</div>}
                        </div>
                        <div className="nt-acts">
                          <label className="toggle" title={rule.is_active ? 'Kapat' : 'Aç'} style={{ marginRight: 8 }}>
                            <input type="checkbox" checked={!!rule.is_active} onChange={() => toggleRule(rule)} />
                            <span className="toggle-slider" />
                          </label>
                          <button className="btn btn-icon" title="Düzenle" onClick={() => openEditRule(rule)}><Ic n="edit" s={18} /></button>
                          <button className="btn btn-icon" title="Sil" style={{ color: 'var(--danger)' }} onClick={() => deleteRule(rule.id)}><Ic n="trash" s={18} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ═══ KANALLAR ═══ */}
          {tab === 'channels' && (
            !channels.length ? (
              <div className="card"><div className="empty-state"><div className="icon"><Ic n="radio" s={44} /></div><p>Henüz kanal yok</p>
                <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => { setShowAddChannel(true); setError(''); }}><Ic n="plus" s={16} />Kanal ekle</button></div></div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="list">
                  {channels.map(c => {
                    const tg = c.type === 'telegram';
                    const used = rules.filter(r => String(r.channel_id) === String(c.id)).length;
                    return (
                      <div key={c.id} className="list-row">
                        <div className="ic-circle" style={{ background: tg ? 'var(--accent-s)' : 'var(--purple-s)', color: tg ? 'var(--link)' : 'var(--purple)' }}><Ic n={tg ? 'send' : 'webhook'} s={20} /></div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="flex-center gap-8"><span className="truncate" style={{ fontWeight: 600 }}>{c.name}</span><span className={`badge badge-${c.is_active ? 'success' : 'neutral'}`}>{c.is_active ? 'Aktif' : 'Pasif'}</span></div>
                          <div className="truncate text-muted" style={{ fontSize: 13 }}>
                            {tg ? <>Telegram · <span className="mono">{c.config?.chat_id}</span></> : <span className="mono">{c.config?.url}</span>}
                            {' · '}{used} kural
                          </div>
                        </div>
                        <button className="btn btn-icon" title="Sil" style={{ color: 'var(--danger)' }} onClick={() => deleteChannel(c.id)}><Ic n="trash" s={18} /></button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )
          )}

          {/* ═══ GEÇMİŞ ═══ */}
          {tab === 'logs' && (
            !logs.length ? (
              <div className="card"><div className="empty-state"><div className="icon"><Ic n="history" s={44} /></div><p>Henüz bildirim gönderilmedi</p></div></div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="list">
                  {logs.map(l => {
                    const t = trigMap[l.trigger_type] || { label: l.trigger_type, ic: 'bell', c: '#6ab3f3' };
                    const ok = l.status === 'sent';
                    return (
                      <div key={l.id} className="list-row">
                        <div className="ic-circle" style={{ width: 38, height: 38, background: t.c + '22', color: t.c }}><Ic n={t.ic} s={18} /></div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="truncate" style={{ fontWeight: 600, fontSize: 14 }}>{t.label}</div>
                          <div className="truncate text-muted" style={{ fontSize: 12.5 }}>{l.bot_name || '—'} · {new Date(l.created_at).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}</div>
                        </div>
                        <span className={`badge badge-${ok ? 'success' : 'danger'}`}><Ic n={ok ? 'check' : 'close'} s={12} sw={2.6} />{ok ? 'Gönderildi' : 'Başarısız'}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )
          )}
        </>
      )}

      {showAddChannel && <ChannelForm channelForm={channelForm} setChannelForm={setChannelForm} saving={saving} error={error} onSubmit={saveChannel} onClose={() => setShowAddChannel(false)} />}
      {(showAddRule || editRule) && <RuleForm ruleForm={ruleForm} setRuleForm={setRuleForm} editRule={editRule} channels={channels} bots={bots} saving={saving} error={error} onSubmit={saveRule} onClose={closeRuleForm} />}
      {bots.length === 0 && !loading && tab === 'rules' && <div className="text-muted text-sm" style={{ marginTop: 10 }}>Kural için önce bir bot ekleyin.</div>}
    </div>
  );
}
